/**
 * Fields Tab for Admin Client Detail Page
 * Full CRUD for CustomFieldDefinition with filtering, detail form, and system field protection.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { ArrowDown, ArrowUp, ChevronDown } from "@untitledui/icons";
import { LoadingIndicator } from "@/components/application/loading-indicator/loading-indicator";
import { Badge } from "@/components/base/badges/badges";
import { Button } from "@/components/base/buttons/button";
import { Input } from "@/components/base/input/input";
import { Select, type SelectItemType } from "@/components/base/select/select";
import { SelectItem } from "@/components/base/select/select-item";
import { TextArea } from "@/components/base/textarea/textarea";
import { Toggle } from "@/components/base/toggle/toggle";
import { useAdminEnums } from "@/hooks/use-admin-enums";
import { ApiException } from "@/services/api";
import { useAdminApi } from "@/services/api/admin-api";
import type {
    CreateCustomFieldDefinitionRequestDto,
    CustomFieldDefinitionAdminDetailDto,
    CustomFieldDefinitionAdminListItemDto,
    DocumentTypeAdminListItemDto,
    LineItemTypeAdminListItemDto,
    OrganizationAdminListItemDto,
    UpdateCustomFieldDefinitionRequestDto,
} from "@/types/api/admin";
import { cx } from "@/utils/cx";
import { CalculationExpressionBuilder } from "./CalculationExpressionBuilder";
import { ValidationRuleBuilder } from "./ValidationRuleBuilder";

// =============================================================================
// TYPES
// =============================================================================

interface FieldsTabProps {
    clientId: number;
}

type ViewMode = "list" | "detail" | "create";

// =============================================================================
// FIELD LIST SECTION
// =============================================================================

function FieldListSection({
    clientId,
    organizationId,
    onSelectField,
    onCreateField,
}: FieldsTabProps & { organizationId: number; onSelectField: (id: number) => void; onCreateField: () => void }) {
    const adminApi = useAdminApi();
    const { getOptions, isLoaded: enumsLoaded } = useAdminEnums();
    const [fields, setFields] = useState<CustomFieldDefinitionAdminListItemDto[]>([]);
    const [total, setTotal] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Filters
    const [entityType, setEntityType] = useState("");
    const [entityId, setEntityId] = useState("");
    const [skip, setSkip] = useState(0);

    // Reference data for cascading entity ID dropdown
    const [documentTypes, setDocumentTypes] = useState<DocumentTypeAdminListItemDto[]>([]);
    const [lineItemTypes, setLineItemTypes] = useState<LineItemTypeAdminListItemDto[]>([]);

    // Load reference data once
    useEffect(() => {
        let cancelled = false;
        async function loadRef() {
            try {
                const [dts, lits] = await Promise.all([adminApi.listDocumentTypes(), adminApi.listLineItemTypes()]);
                if (!cancelled) {
                    setDocumentTypes(dts);
                    setLineItemTypes(lits);
                }
            } catch {
                // Non-critical
            }
        }
        loadRef();
        return () => {
            cancelled = true;
        };
    }, [adminApi]);

    const fetchFields = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const res = await adminApi.listFieldDefinitions(clientId, organizationId, {
                entityType: entityType || undefined,
                entityId: entityId ? Number(entityId) : undefined,
                pageSize: 50,
                skip,
            });
            setFields(res.data);
            setTotal(res.total);
        } catch (err) {
            setError(err instanceof Error ? err.message : "Failed to load fields.");
        } finally {
            setIsLoading(false);
        }
    }, [adminApi, clientId, organizationId, entityType, entityId, skip]);

    useEffect(() => {
        fetchFields();
    }, [fetchFields]);

    // Reset entityId when entityType changes
    useEffect(() => {
        setEntityId("");
        setSkip(0);
    }, [entityType]);

    const entityTypeOptions: SelectItemType[] = enumsLoaded ? getOptions("CustomFieldEntityTypeEnum").map((opt) => ({ id: opt.label, label: opt.label })) : [];

    // Cascading: show Document or LineItem types based on entityType
    const entityIdOptions: SelectItemType[] =
        entityType === "Document"
            ? documentTypes.map((dt) => ({ id: String(dt.id), label: `${dt.name} (${dt.code})` }))
            : entityType === "LineItem"
              ? lineItemTypes.map((lit) => ({ id: String(lit.id), label: `${lit.name} (${lit.code})` }))
              : [];

    const PAGE_SIZE = 50;
    const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
    const currentPage = Math.floor(skip / PAGE_SIZE) + 1;

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
                <div>
                    <h3 className="text-lg font-semibold text-primary">Field Definitions</h3>
                    <p className="mt-1 text-sm text-tertiary">Custom field definitions for this client. System fields are shared across all clients.</p>
                </div>
                <Button color="primary" size="sm" onClick={onCreateField}>
                    Add Field
                </Button>
            </div>

            {/* Filters */}
            <div className="flex gap-3">
                <div className="w-48">
                    {entityTypeOptions.length > 0 && (
                        <Select
                            label="Entity Type"
                            items={entityTypeOptions}
                            selectedKey={entityType || null}
                            onSelectionChange={(key) => setEntityType(String(key ?? ""))}
                            placeholder="All types"
                            size="sm"
                        >
                            {(item) => <SelectItem id={item.id}>{item.label}</SelectItem>}
                        </Select>
                    )}
                </div>
                {entityType && entityIdOptions.length > 0 && (
                    <div className="w-64">
                        <Select
                            label={entityType === "Document" ? "Document Type" : "Line Item Type"}
                            items={entityIdOptions}
                            selectedKey={entityId || null}
                            onSelectionChange={(key) => {
                                setEntityId(String(key ?? ""));
                                setSkip(0);
                            }}
                            placeholder="All"
                            size="sm"
                        >
                            {(item) => <SelectItem id={item.id}>{item.label}</SelectItem>}
                        </Select>
                    </div>
                )}
                {entityType && (
                    <div className="flex items-end">
                        <Button
                            color="secondary"
                            size="sm"
                            onClick={() => {
                                setEntityType("");
                                setEntityId("");
                                setSkip(0);
                            }}
                        >
                            Clear Filters
                        </Button>
                    </div>
                )}
            </div>

            {/* Table */}
            {isLoading ? (
                <div className="py-8">
                    <LoadingIndicator size="sm" label="Loading fields..." />
                </div>
            ) : error ? (
                <p className="text-sm text-error-primary">{error}</p>
            ) : fields.length === 0 ? (
                <p className="py-8 text-center text-sm text-quaternary">No field definitions found.</p>
            ) : (
                <>
                    <FieldGroupedTable
                        fields={fields}
                        clientId={clientId}
                        organizationId={organizationId}
                        onSelectField={onSelectField}
                        onFieldsReordered={fetchFields}
                    />

                    {total > PAGE_SIZE && (
                        <div className="flex items-center justify-between">
                            <p className="text-sm text-tertiary">
                                Showing {skip + 1}–{Math.min(skip + PAGE_SIZE, total)} of {total}
                            </p>
                            <div className="flex gap-2">
                                <Button color="secondary" size="sm" isDisabled={skip === 0} onClick={() => setSkip(Math.max(0, skip - PAGE_SIZE))}>
                                    Previous
                                </Button>
                                <Button color="secondary" size="sm" isDisabled={currentPage >= totalPages} onClick={() => setSkip(skip + PAGE_SIZE)}>
                                    Next
                                </Button>
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}

// =============================================================================
// GROUPED TABLE WITH REORDER
// =============================================================================

interface FieldGroup {
    key: string;
    label: string;
    fields: CustomFieldDefinitionAdminListItemDto[];
}

interface EntityTypeSection {
    entityType: string;
    label: string;
    totalFields: number;
    groups: FieldGroup[];
}

function FieldGroupedTable({
    fields,
    clientId,
    organizationId,
    onSelectField,
    onFieldsReordered,
}: {
    fields: CustomFieldDefinitionAdminListItemDto[];
    clientId: number;
    organizationId: number;
    onSelectField: (id: number) => void;
    onFieldsReordered: () => void;
}) {
    const adminApi = useAdminApi();
    const [isSaving, setIsSaving] = useState(false);
    const [collapsedSections, setCollapsedSections] = useState<Set<string>>(new Set());

    const toggleSection = useCallback((key: string) => {
        setCollapsedSections((prev) => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    }, []);

    const sections = useMemo<EntityTypeSection[]>(() => {
        const groupMap = new Map<string, FieldGroup>();
        for (const field of fields) {
            const name = field.entityName ?? `ID ${field.entityId ?? "?"}`;
            const key = `${field.entityType}:${field.entityId ?? "null"}`;
            const label = name;
            if (!groupMap.has(key)) {
                groupMap.set(key, { key, label, fields: [] });
            }
            groupMap.get(key)!.fields.push(field);
        }

        const sectionMap = new Map<string, EntityTypeSection>();
        for (const [, group] of groupMap) {
            const entityType = group.fields[0].entityType;
            if (!sectionMap.has(entityType)) {
                sectionMap.set(entityType, {
                    entityType,
                    label: entityType === "Document" ? "Document Fields" : "Line Item Fields",
                    totalFields: 0,
                    groups: [],
                });
            }
            const section = sectionMap.get(entityType)!;
            section.groups.push(group);
            section.totalFields += group.fields.length;
        }

        // Document first, then LineItem
        const order = ["Document", "LineItem"];
        return order.filter((t) => sectionMap.has(t)).map((t) => sectionMap.get(t)!);
    }, [fields]);

    const handleMoveUp = useCallback(
        async (group: FieldGroup, index: number) => {
            if (index === 0 || isSaving) return;
            const arr = [...group.fields];
            [arr[index - 1], arr[index]] = [arr[index], arr[index - 1]];
            const items = arr.map((f, i) => ({ id: f.id, displayOrder: i }));
            setIsSaving(true);
            try {
                await adminApi.reorderFieldDefinitions(clientId, organizationId, items);
                onFieldsReordered();
            } catch {
                // Silently fail — list will refresh
            } finally {
                setIsSaving(false);
            }
        },
        [adminApi, clientId, organizationId, isSaving, onFieldsReordered],
    );

    const handleMoveDown = useCallback(
        async (group: FieldGroup, index: number) => {
            if (index >= group.fields.length - 1 || isSaving) return;
            const arr = [...group.fields];
            [arr[index], arr[index + 1]] = [arr[index + 1], arr[index]];
            const items = arr.map((f, i) => ({ id: f.id, displayOrder: i }));
            setIsSaving(true);
            try {
                await adminApi.reorderFieldDefinitions(clientId, organizationId, items);
                onFieldsReordered();
            } catch {
                // Silently fail — list will refresh
            } finally {
                setIsSaving(false);
            }
        },
        [adminApi, clientId, organizationId, isSaving, onFieldsReordered],
    );

    return (
        <div className="space-y-6">
            {sections.map((section) => {
                const isCollapsed = collapsedSections.has(section.entityType);
                return (
                    <div key={section.entityType} className="rounded-xl border border-secondary">
                        <button className="flex w-full cursor-pointer items-center gap-3 px-5 py-3" onClick={() => toggleSection(section.entityType)}>
                            <ChevronDown className={cx("size-5 text-tertiary transition-transform duration-200", isCollapsed && "-rotate-90")} />
                            <h4 className="text-md font-semibold text-primary">{section.label}</h4>
                            <span className="text-xs text-tertiary">
                                ({section.totalFields} field{section.totalFields !== 1 ? "s" : ""} across {section.groups.length} type
                                {section.groups.length !== 1 ? "s" : ""})
                            </span>
                        </button>

                        {!isCollapsed && (
                            <div className="space-y-4 px-5 pb-5">
                                {section.groups.map((group) => (
                                    <div key={group.key} className="overflow-x-auto rounded-lg border border-secondary">
                                        <div className="border-b border-secondary bg-secondary_subtle px-4 py-2">
                                            <span className="text-sm font-semibold text-primary">{group.label}</span>
                                            <span className="ml-2 text-xs text-tertiary">({group.fields.length} fields)</span>
                                        </div>
                                        <table className="w-full text-left text-sm">
                                            <thead className="border-b border-secondary bg-secondary_subtle/50">
                                                <tr>
                                                    <th className="w-8 px-2 py-2" />
                                                    <th className="px-4 py-2 font-medium text-secondary">Code</th>
                                                    <th className="px-4 py-2 font-medium text-secondary">Label</th>
                                                    <th className="px-4 py-2 font-medium text-secondary">Type</th>
                                                    <th className="px-4 py-2 font-medium text-secondary">Flags</th>
                                                    <th className="px-4 py-2 font-medium text-secondary">Order</th>
                                                    <th className="px-4 py-2 font-medium text-secondary">Scope</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-secondary">
                                                {group.fields.map((field, idx) => (
                                                    <tr key={field.id} className="group cursor-pointer hover:bg-primary_hover">
                                                        <td className="px-1 py-2" onClick={(e) => e.stopPropagation()}>
                                                            {field.organizationId != null && (
                                                                <div className="flex flex-col items-center opacity-0 group-hover:opacity-100">
                                                                    <button
                                                                        className="cursor-pointer rounded p-0.5 text-tertiary hover:text-primary disabled:cursor-not-allowed disabled:opacity-30"
                                                                        disabled={idx === 0 || isSaving}
                                                                        onClick={() => handleMoveUp(group, idx)}
                                                                    >
                                                                        <ArrowUp className="size-3.5" />
                                                                    </button>
                                                                    <button
                                                                        className="cursor-pointer rounded p-0.5 text-tertiary hover:text-primary disabled:cursor-not-allowed disabled:opacity-30"
                                                                        disabled={idx >= group.fields.length - 1 || isSaving}
                                                                        onClick={() => handleMoveDown(group, idx)}
                                                                    >
                                                                        <ArrowDown className="size-3.5" />
                                                                    </button>
                                                                </div>
                                                            )}
                                                        </td>
                                                        <td className="px-4 py-2 font-mono text-sm text-primary" onClick={() => onSelectField(field.id)}>
                                                            {field.fieldCode}
                                                        </td>
                                                        <td className="px-4 py-2 text-tertiary" onClick={() => onSelectField(field.id)}>
                                                            {field.fieldLabel}
                                                        </td>
                                                        <td className="px-4 py-2 text-tertiary" onClick={() => onSelectField(field.id)}>
                                                            {field.fieldType}
                                                        </td>
                                                        <td className="px-4 py-2" onClick={() => onSelectField(field.id)}>
                                                            <div className="flex flex-wrap gap-1">
                                                                {field.isRequired && <FlagBadge label="Req" />}
                                                                {field.isGenerated && <FlagBadge label="AI" />}
                                                                {field.isCalculated && <FlagBadge label="Calc" />}
                                                                {field.isExportable && <FlagBadge label="Exp" />}
                                                                {field.isHardcoded && <FlagBadge label="Hard" />}
                                                            </div>
                                                        </td>
                                                        <td className="px-4 py-2 text-tertiary" onClick={() => onSelectField(field.id)}>
                                                            {field.displayOrder}
                                                        </td>
                                                        <td className="px-4 py-2" onClick={() => onSelectField(field.id)}>
                                                            <Badge color={field.organizationId != null ? "brand" : "gray"} size="sm" type="pill-color">
                                                                {field.organizationId != null ? "Org" : "System"}
                                                            </Badge>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
}

function FlagBadge({ label }: { label: string }) {
    return (
        <Badge color="gray" size="sm" type="pill-color">
            {label}
        </Badge>
    );
}

// =============================================================================
// FIELD DETAIL / CREATE FORM
// =============================================================================

function FieldDetailForm({
    clientId,
    organizationId,
    fieldId,
    isCreate,
    onBack,
    onSaved,
}: {
    clientId: number;
    organizationId: number;
    fieldId?: number;
    isCreate: boolean;
    onBack: () => void;
    onSaved: () => void;
}) {
    const adminApi = useAdminApi();
    const { getOptions, isLoaded: enumsLoaded } = useAdminEnums();

    const [detail, setDetail] = useState<CustomFieldDefinitionAdminDetailDto | null>(null);
    const [isLoading, setIsLoading] = useState(!isCreate);
    const [error, setError] = useState<string | null>(null);
    const [isSaving, setIsSaving] = useState(false);
    const [saveError, setSaveError] = useState<string | null>(null);
    const [saveSuccess, setSaveSuccess] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    // Which input a 409 conflict maps to, so it can be highlighted inline.
    const [conflictField, setConflictField] = useState<"code" | "export" | null>(null);

    // Reference data
    const [documentTypes, setDocumentTypes] = useState<DocumentTypeAdminListItemDto[]>([]);
    const [lineItemTypes, setLineItemTypes] = useState<LineItemTypeAdminListItemDto[]>([]);

    // Form fields
    const [fieldCode, setFieldCode] = useState("");
    const [fieldLabel, setFieldLabel] = useState("");
    const [fieldType, setFieldType] = useState("");
    const [formEntityType, setFormEntityType] = useState("");
    const [formEntityId, setFormEntityId] = useState("");
    const [isRequired, setIsRequired] = useState(false);
    const [isVisible, setIsVisible] = useState(true);
    const [isExportable, setIsExportable] = useState(false);
    const [isGenerated, setIsGenerated] = useState(false);
    const [isCalculated, setIsCalculated] = useState(false);
    const [isHardcoded, setIsHardcoded] = useState(false);
    const [isSearchable, setIsSearchable] = useState(false);
    const [isIndexed, setIsIndexed] = useState(false);
    const [isExportedAsCustom, setIsExportedAsCustom] = useState(false);
    const [isCalculatedAfterChild, setIsCalculatedAfterChild] = useState(false);
    const [exportFieldName, setExportFieldName] = useState("");
    const [databaseType, setDatabaseType] = useState("");
    const [calculationExpression, setCalculationExpression] = useState("");
    const [exportedAsCustomIn, setExportedAsCustomIn] = useState("");
    const [validationRules, setValidationRules] = useState("");
    const [defaultValue, setDefaultValue] = useState("");
    const [helpText, setHelpText] = useState("");
    const [llmDescription, setLlmDescription] = useState("");
    const [entitlementCode, setEntitlementCode] = useState("");
    const [displayOrder, setDisplayOrder] = useState("0");

    const isSystemField = detail != null && detail.organizationId == null;

    // Load reference data
    useEffect(() => {
        let cancelled = false;
        async function loadRef() {
            try {
                const [dts, lits] = await Promise.all([adminApi.listDocumentTypes(), adminApi.listLineItemTypes()]);
                if (!cancelled) {
                    setDocumentTypes(dts);
                    setLineItemTypes(lits);
                }
            } catch {
                // Non-critical
            }
        }
        loadRef();
        return () => {
            cancelled = true;
        };
    }, [adminApi]);

    // Load field detail
    useEffect(() => {
        if (isCreate || !fieldId) return;
        let cancelled = false;
        async function loadDetail() {
            setIsLoading(true);
            setError(null);
            try {
                const data = await adminApi.getFieldDefinition(clientId, organizationId, fieldId!);
                if (!cancelled) {
                    setDetail(data);
                    // Populate form
                    setFieldCode(data.fieldCode);
                    setFieldLabel(data.fieldLabel);
                    setFieldType(data.fieldType);
                    setFormEntityType(data.entityType);
                    setFormEntityId(data.entityId != null ? String(data.entityId) : "");
                    setIsRequired(data.isRequired);
                    setIsVisible(data.isVisible);
                    setIsExportable(data.isExportable);
                    setIsGenerated(data.isGenerated);
                    setIsCalculated(data.isCalculated);
                    setIsHardcoded(data.isHardcoded);
                    setIsSearchable(data.isSearchable);
                    setIsIndexed(data.isIndexed);
                    setIsExportedAsCustom(data.isExportedAsCustom);
                    setIsCalculatedAfterChild(data.isCalculatedAfterChild);
                    setExportFieldName(data.exportFieldName ?? "");
                    setDatabaseType(data.databaseType ?? "");
                    setCalculationExpression(data.calculationExpression ?? "");
                    setExportedAsCustomIn(data.exportedAsCustomIn ?? "");
                    setValidationRules(data.validationRules ?? "");
                    setDefaultValue(data.defaultValue ?? "");
                    setHelpText(data.helpText ?? "");
                    setLlmDescription(data.llmDescription ?? "");
                    setEntitlementCode(data.entitlementCode ?? "");
                    setDisplayOrder(String(data.displayOrder));
                }
            } catch (err) {
                if (!cancelled) setError(err instanceof Error ? err.message : "Failed to load field.");
            } finally {
                if (!cancelled) setIsLoading(false);
            }
        }
        loadDetail();
        return () => {
            cancelled = true;
        };
    }, [adminApi, clientId, organizationId, fieldId, isCreate]);

    const entityTypeOptions: SelectItemType[] = enumsLoaded ? getOptions("CustomFieldEntityTypeEnum").map((opt) => ({ id: opt.label, label: opt.label })) : [];

    const fieldTypeOptions: SelectItemType[] = enumsLoaded ? getOptions("FieldTypeEnum").map((opt) => ({ id: opt.label, label: opt.label })) : [];

    const entityIdOptions: SelectItemType[] =
        formEntityType === "Document"
            ? documentTypes.map((dt) => ({ id: String(dt.id), label: `${dt.name} (${dt.code})` }))
            : formEntityType === "LineItem"
              ? lineItemTypes.map((lit) => ({ id: String(lit.id), label: `${lit.name} (${lit.code})` }))
              : [];

    // Resolve entity name from loaded reference data (works even before backend redeploy)
    const resolvedEntityName = useMemo(() => {
        if (!formEntityId) return null;
        const id = Number(formEntityId);
        if (formEntityType === "Document") {
            const dt = documentTypes.find((d) => d.id === id);
            if (dt) return `${dt.name} (${dt.code})`;
        } else if (formEntityType === "LineItem") {
            const lit = lineItemTypes.find((l) => l.id === id);
            if (lit) return `${lit.name} (${lit.code})`;
        }
        return detail?.entityName ?? null;
    }, [formEntityType, formEntityId, documentTypes, lineItemTypes, detail]);

    const handleSave = useCallback(async () => {
        setIsSaving(true);
        setSaveError(null);
        setSaveSuccess(false);
        setConflictField(null);
        try {
            if (isCreate) {
                const dto: CreateCustomFieldDefinitionRequestDto = {
                    entityType: formEntityType,
                    entityId: formEntityId ? Number(formEntityId) : null,
                    fieldCode: fieldCode.trim(),
                    fieldLabel: fieldLabel.trim(),
                    fieldType,
                    isRequired,
                    isVisible,
                    isExportable,
                    isGenerated,
                    isCalculated,
                    isHardcoded,
                    isSearchable,
                    isIndexed,
                    isExportedAsCustom,
                    isCalculatedAfterChild,
                    exportFieldName: exportFieldName || null,
                    databaseType: databaseType || null,
                    calculationExpression: calculationExpression || null,
                    exportedAsCustomIn: exportedAsCustomIn || null,
                    validationRules: validationRules || null,
                    defaultValue: defaultValue || null,
                    helpText: helpText || null,
                    llmDescription: llmDescription || null,
                    entitlementCode: entitlementCode || null,
                    displayOrder: Number(displayOrder) || 0,
                };
                await adminApi.createFieldDefinition(clientId, organizationId, dto);
                onSaved();
                onBack();
            } else if (fieldId) {
                const dto: UpdateCustomFieldDefinitionRequestDto = {
                    fieldLabel: fieldLabel.trim(),
                    fieldType,
                    isRequired,
                    isVisible,
                    isExportable,
                    isGenerated,
                    isCalculated,
                    isHardcoded,
                    isSearchable,
                    isIndexed,
                    isExportedAsCustom,
                    isCalculatedAfterChild,
                    exportFieldName: exportFieldName || null,
                    databaseType: databaseType || null,
                    calculationExpression: calculationExpression || null,
                    exportedAsCustomIn: exportedAsCustomIn || null,
                    validationRules: validationRules || null,
                    defaultValue: defaultValue || null,
                    helpText: helpText || null,
                    llmDescription: llmDescription || null,
                    entitlementCode: entitlementCode || null,
                    displayOrder: Number(displayOrder) || 0,
                };
                await adminApi.updateFieldDefinition(clientId, organizationId, fieldId, dto);
                setSaveSuccess(true);
                setTimeout(() => setSaveSuccess(false), 3000);
                onSaved();
            }
        } catch (err) {
            const message = err instanceof Error ? err.message : "Failed to save field.";
            setSaveError(message);
            // A 409 is a uniqueness conflict — flag the offending input so it's highlighted inline.
            if (err instanceof ApiException && err.status === 409) {
                if (message.startsWith("Export field name")) setConflictField("export");
                else if (message.includes("already exists for this entity")) setConflictField("code");
            }
        } finally {
            setIsSaving(false);
        }
    }, [
        adminApi,
        clientId,
        organizationId,
        fieldId,
        isCreate,
        onBack,
        onSaved,
        formEntityType,
        formEntityId,
        fieldCode,
        fieldLabel,
        fieldType,
        isRequired,
        isVisible,
        isExportable,
        isGenerated,
        isCalculated,
        isHardcoded,
        isSearchable,
        isIndexed,
        isExportedAsCustom,
        isCalculatedAfterChild,
        exportFieldName,
        databaseType,
        calculationExpression,
        exportedAsCustomIn,
        validationRules,
        defaultValue,
        helpText,
        llmDescription,
        entitlementCode,
        displayOrder,
    ]);

    const handleDelete = useCallback(async () => {
        if (!fieldId) return;
        setIsSaving(true);
        setSaveError(null);
        try {
            await adminApi.deleteFieldDefinition(clientId, organizationId, fieldId);
            onSaved();
            onBack();
        } catch (err) {
            setSaveError(err instanceof Error ? err.message : "Failed to delete field.");
        } finally {
            setIsSaving(false);
            setShowDeleteConfirm(false);
        }
    }, [adminApi, clientId, organizationId, fieldId, onBack, onSaved]);

    if (isLoading) {
        return (
            <div className="py-8">
                <LoadingIndicator size="sm" label="Loading field..." />
            </div>
        );
    }

    if (error) {
        return (
            <div className="space-y-4">
                <Button color="secondary" size="sm" onClick={onBack}>
                    Back to list
                </Button>
                <p className="text-sm text-error-primary">{error}</p>
            </div>
        );
    }

    const disabled = isSystemField;

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <Button color="secondary" size="sm" onClick={onBack}>
                        Back to list
                    </Button>
                    <h3 className="text-lg font-semibold text-primary">{isCreate ? "Create Field Definition" : `Field: ${fieldCode}`}</h3>
                    {!isCreate && (
                        <Badge color={isSystemField ? "gray" : "brand"} size="sm" type="pill-color">
                            {isSystemField ? "System" : "Client"}
                        </Badge>
                    )}
                </div>
                {!isCreate && !isSystemField && (
                    <Button color="tertiary-destructive" size="sm" onClick={() => setShowDeleteConfirm(true)}>
                        Delete
                    </Button>
                )}
            </div>

            {isSystemField && (
                <div className="border-warning bg-warning_subtle rounded-lg border px-4 py-3 text-sm text-warning-primary">
                    This is a system field and cannot be edited or deleted.
                </div>
            )}

            {/* Delete confirmation */}
            {showDeleteConfirm && (
                <div className="bg-error_subtle rounded-lg border border-error p-4">
                    <p className="text-sm text-error-primary">
                        Delete field <span className="font-mono font-semibold">{fieldCode}</span>? This cannot be undone.
                    </p>
                    <div className="mt-3 flex gap-2">
                        <Button color="primary-destructive" size="sm" onClick={handleDelete} isDisabled={isSaving}>
                            {isSaving ? "Deleting..." : "Confirm Delete"}
                        </Button>
                        <Button color="secondary" size="sm" onClick={() => setShowDeleteConfirm(false)} isDisabled={isSaving}>
                            Cancel
                        </Button>
                    </div>
                </div>
            )}

            <div className="max-w-2xl space-y-8">
                {/* Core Fields */}
                <FormSection title="Core">
                    <div className="grid grid-cols-1 gap-4">
                        {isCreate ? (
                            <Input
                                label="Field Code"
                                value={fieldCode}
                                onChange={(v) => {
                                    setFieldCode(v);
                                    if (conflictField === "code") setConflictField(null);
                                }}
                                size="sm"
                                isRequired
                                isDisabled={disabled}
                                isInvalid={conflictField === "code"}
                                hint={conflictField === "code" ? (saveError ?? undefined) : undefined}
                            />
                        ) : (
                            <div>
                                <label className="mb-1.5 block text-sm font-medium text-secondary">Field Code</label>
                                <div className="rounded-lg bg-disabled_subtle px-3 py-2 font-mono text-sm text-disabled">{fieldCode}</div>
                            </div>
                        )}
                        <Input label="Field Label" value={fieldLabel} onChange={setFieldLabel} size="sm" isRequired isDisabled={disabled} />
                        {fieldTypeOptions.length > 0 && (
                            <Select
                                label="Field Type"
                                items={fieldTypeOptions}
                                selectedKey={fieldType || null}
                                onSelectionChange={(key) => setFieldType(String(key ?? ""))}
                                placeholder="Select..."
                                size="sm"
                                isRequired
                                isDisabled={disabled}
                            >
                                {(item) => <SelectItem id={item.id}>{item.label}</SelectItem>}
                            </Select>
                        )}
                        {isCreate ? (
                            <>
                                {entityTypeOptions.length > 0 && (
                                    <Select
                                        label="Entity Type"
                                        items={entityTypeOptions}
                                        selectedKey={formEntityType || null}
                                        onSelectionChange={(key) => {
                                            setFormEntityType(String(key ?? ""));
                                            setFormEntityId("");
                                        }}
                                        placeholder="Select..."
                                        size="sm"
                                        isRequired
                                    >
                                        {(item) => <SelectItem id={item.id}>{item.label}</SelectItem>}
                                    </Select>
                                )}
                                {formEntityType && entityIdOptions.length > 0 && (
                                    <Select
                                        label={formEntityType === "Document" ? "Document Type" : "Line Item Type"}
                                        items={entityIdOptions}
                                        selectedKey={formEntityId || null}
                                        onSelectionChange={(key) => setFormEntityId(String(key ?? ""))}
                                        placeholder="Select..."
                                        size="sm"
                                    >
                                        {(item) => <SelectItem id={item.id}>{item.label}</SelectItem>}
                                    </Select>
                                )}
                            </>
                        ) : (
                            <>
                                <div>
                                    <label className="mb-1.5 block text-sm font-medium text-secondary">Entity Type</label>
                                    <div className="rounded-lg bg-disabled_subtle px-3 py-2 text-sm text-disabled">{formEntityType}</div>
                                </div>
                                <div>
                                    <label className="mb-1.5 block text-sm font-medium text-secondary">
                                        {formEntityType === "Document" ? "Document Type" : formEntityType === "LineItem" ? "Line Item Type" : "Entity"}
                                    </label>
                                    <div className="rounded-lg bg-disabled_subtle px-3 py-2 text-sm text-disabled">
                                        {resolvedEntityName ?? (formEntityId ? `ID ${formEntityId}` : "—")}
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </FormSection>

                {/* Behavior Flags */}
                <FormSection title="Behavior Flags">
                    <div className="grid grid-cols-2 gap-4">
                        <Toggle label="Required" isSelected={isRequired} onChange={setIsRequired} size="sm" isDisabled={disabled} />
                        <Toggle label="Visible" isSelected={isVisible} onChange={setIsVisible} size="sm" isDisabled={disabled} />
                        <Toggle label="Searchable" isSelected={isSearchable} onChange={setIsSearchable} size="sm" isDisabled={disabled} />
                        <Toggle label="Indexed" isSelected={isIndexed} onChange={setIsIndexed} size="sm" isDisabled={disabled} />
                        <Toggle label="Generated (AI)" isSelected={isGenerated} onChange={setIsGenerated} size="sm" isDisabled={disabled} />
                        <Toggle label="Hardcoded" isSelected={isHardcoded} onChange={setIsHardcoded} size="sm" isDisabled={disabled} />
                    </div>
                </FormSection>

                {/* Export */}
                <FormSection title="Export">
                    <div className="grid grid-cols-1 gap-4">
                        <Toggle label="Exportable" isSelected={isExportable} onChange={setIsExportable} size="sm" isDisabled={disabled} />
                        {isExportable && (
                            <>
                                <Input
                                    label="Export Field Name"
                                    value={exportFieldName}
                                    onChange={(v) => {
                                        setExportFieldName(v);
                                        if (conflictField === "export") setConflictField(null);
                                    }}
                                    size="sm"
                                    isDisabled={disabled}
                                    placeholder="Target system column name"
                                    isInvalid={conflictField === "export"}
                                    hint={conflictField === "export" ? (saveError ?? undefined) : undefined}
                                />
                                <Input
                                    label="Database Type"
                                    value={databaseType}
                                    onChange={setDatabaseType}
                                    size="sm"
                                    isDisabled={disabled}
                                    placeholder="e.g. nvarchar(100), decimal(18,6)"
                                />
                            </>
                        )}
                        <Toggle label="Exported as Custom" isSelected={isExportedAsCustom} onChange={setIsExportedAsCustom} size="sm" isDisabled={disabled} />
                        {isExportedAsCustom && (
                            <Input
                                label="Exported as Custom In"
                                value={exportedAsCustomIn}
                                onChange={setExportedAsCustomIn}
                                size="sm"
                                isDisabled={disabled}
                                placeholder="Container field name"
                            />
                        )}
                    </div>
                </FormSection>

                {/* Calculation */}
                <FormSection title="Calculation">
                    <div className="grid grid-cols-1 gap-4">
                        <Toggle label="Calculated" isSelected={isCalculated} onChange={setIsCalculated} size="sm" isDisabled={disabled} />
                        {isCalculated && (
                            <>
                                <CalculationExpressionBuilder value={calculationExpression} onChange={setCalculationExpression} isDisabled={disabled} />
                                <Toggle
                                    label="Calculated After Child"
                                    isSelected={isCalculatedAfterChild}
                                    onChange={setIsCalculatedAfterChild}
                                    size="sm"
                                    isDisabled={disabled}
                                />
                            </>
                        )}
                    </div>
                </FormSection>

                {/* AI & Defaults */}
                <FormSection title="AI & Defaults">
                    <div className="grid grid-cols-1 gap-4">
                        <TextArea
                            label="LLM Description"
                            value={llmDescription}
                            onChange={setLlmDescription}
                            isDisabled={disabled}
                            placeholder="AI-optimized field description for extraction"
                        />
                        <Input label="Default Value" value={defaultValue} onChange={setDefaultValue} size="sm" isDisabled={disabled} />
                        <Input label="Help Text" value={helpText} onChange={setHelpText} size="sm" isDisabled={disabled} />
                    </div>
                </FormSection>

                {/* Gating & Order */}
                <FormSection title="Gating & Display">
                    <div className="grid grid-cols-1 gap-4">
                        <Input
                            label="Entitlement Code"
                            value={entitlementCode}
                            onChange={setEntitlementCode}
                            size="sm"
                            isDisabled={disabled}
                            placeholder="e.g. FIELD_TAX"
                        />
                        <Input label="Display Order" value={displayOrder} onChange={setDisplayOrder} size="sm" type="number" isDisabled={disabled} />
                    </div>
                </FormSection>

                {/* Validation */}
                <FormSection title="Validation Rules">
                    <ValidationRuleBuilder value={validationRules} onChange={setValidationRules} isDisabled={disabled} />
                </FormSection>

                {/* Save / Error / Success */}
                {saveError && <p className="text-sm text-error-primary">{saveError}</p>}
                {saveSuccess && <p className="text-sm text-success-primary">Field updated successfully.</p>}

                {!isSystemField && (
                    <div className="flex gap-2">
                        <Button
                            color="primary"
                            size="sm"
                            onClick={handleSave}
                            isDisabled={isSaving || !fieldLabel.trim() || !fieldType || (isCreate && (!fieldCode.trim() || !formEntityType))}
                        >
                            {isSaving ? "Saving..." : isCreate ? "Create Field" : "Save Changes"}
                        </Button>
                        <Button color="secondary" size="sm" onClick={onBack} isDisabled={isSaving}>
                            Cancel
                        </Button>
                    </div>
                )}
            </div>
        </div>
    );
}

function FormSection({ title, children }: { title: string; children: React.ReactNode }) {
    return (
        <div>
            <h4 className="mb-3 border-b border-secondary pb-2 text-sm font-semibold text-secondary">{title}</h4>
            {children}
        </div>
    );
}

// =============================================================================
// FIELDS TAB
// =============================================================================

export function FieldsTab({ clientId }: FieldsTabProps) {
    const adminApi = useAdminApi();

    const [viewMode, setViewMode] = useState<ViewMode>("list");
    const [selectedFieldId, setSelectedFieldId] = useState<number | null>(null);
    const [listKey, setListKey] = useState(0);

    // Organization selector state
    const [organizations, setOrganizations] = useState<OrganizationAdminListItemDto[]>([]);
    const [selectedOrgId, setSelectedOrgId] = useState<number | null>(null);
    const [isOrgsLoading, setIsOrgsLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;
        async function loadOrgs() {
            setIsOrgsLoading(true);
            try {
                const orgs = await adminApi.listClientOrganizations(clientId);
                if (cancelled) return;
                const active = orgs.filter((o: OrganizationAdminListItemDto) => o.isActive);
                setOrganizations(active);
                if (active.length > 0) setSelectedOrgId(active[0].id);
            } catch {
                // Non-critical — org selector will be empty
            } finally {
                if (!cancelled) setIsOrgsLoading(false);
            }
        }
        loadOrgs();
        return () => {
            cancelled = true;
        };
    }, [adminApi, clientId]);

    const orgOptions: SelectItemType[] = useMemo(() => organizations.map((o) => ({ id: String(o.id), label: `${o.name} (${o.code})` })), [organizations]);

    const handleSelectField = useCallback((id: number) => {
        setSelectedFieldId(id);
        setViewMode("detail");
    }, []);

    const handleCreateField = useCallback(() => {
        setSelectedFieldId(null);
        setViewMode("create");
    }, []);

    const handleBack = useCallback(() => {
        setViewMode("list");
        setSelectedFieldId(null);
    }, []);

    const handleSaved = useCallback(() => {
        setListKey((k) => k + 1);
    }, []);

    if (isOrgsLoading) return <LoadingIndicator size="sm" />;

    if (organizations.length === 0) {
        return (
            <div className="space-y-2">
                <h3 className="text-lg font-semibold text-primary">Field Definitions</h3>
                <p className="text-sm text-tertiary">No organizations configured. Create an organization first.</p>
            </div>
        );
    }

    if (selectedOrgId == null) return null;

    // Organization selector shown above all views
    const orgSelector = (
        <div className="mb-4 max-w-xs">
            <Select
                label="Organization"
                items={orgOptions}
                selectedKey={String(selectedOrgId)}
                onSelectionChange={(k) => {
                    setSelectedOrgId(k ? Number(k) : null);
                    setViewMode("list");
                    setSelectedFieldId(null);
                    setListKey((prev) => prev + 1);
                }}
                placeholder="Select organization..."
                size="sm"
            >
                {(item: SelectItemType) => <SelectItem id={item.id}>{item.label}</SelectItem>}
            </Select>
        </div>
    );

    if (viewMode === "detail" && selectedFieldId != null) {
        return (
            <>
                {orgSelector}
                <FieldDetailForm
                    clientId={clientId}
                    organizationId={selectedOrgId}
                    fieldId={selectedFieldId}
                    isCreate={false}
                    onBack={handleBack}
                    onSaved={handleSaved}
                />
            </>
        );
    }

    if (viewMode === "create") {
        return (
            <>
                {orgSelector}
                <FieldDetailForm clientId={clientId} organizationId={selectedOrgId} isCreate={true} onBack={handleBack} onSaved={handleSaved} />
            </>
        );
    }

    return (
        <>
            {orgSelector}
            <FieldListSection
                key={listKey}
                clientId={clientId}
                organizationId={selectedOrgId}
                onSelectField={handleSelectField}
                onCreateField={handleCreateField}
            />
        </>
    );
}
